import React, {useEffect, useMemo, useState} from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Select,
  Text,
  Textarea,
  useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import Loading from "../../../components/Loading/Loading";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { checkErrors, hasError } from "../../../utils/forms-utils";
import SearchSelect from "../../../components/SearchSelect/SearchSelect";
import { processesService } from "../../../services/processesService";
import { departmentService } from "../../../services/departmentService";
import { fromServerDate, toServerDate } from "../../../utils/traffic-violation-utils";
import DateTime from "react-datetime";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

function ProcessForward({data}) {
  const textColor = useColorModeValue("gray.700", "white");
  const bgTextarea = useColorModeValue("white", "navy.900");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const { id } = useParams();

  const [inputError, setInputError] = useState({});
  const [userData, setUserData] = useState([]);
  const [processData, setProcessData] = useState([]);
  const [departmentsData, setDepartmentsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
 

  const ids = useMemo(() => ({
    department: "process-forward-department",
    user: "process-forward-user",
    judgementLimitDate: "process-forward-judgment-limit-date",
    observation: "process-forward-observation",
  }), []);

  const [formData, setFormData] = useState( {
    department: "",
    user: "",
    judgementLimitDate: "",
    observation: ""
  });

  const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

  const initFormData = (data) => ({
    [ids.department]: {
      value: "",
      validations: [{ required: true }],
    },
    [ids.user]: { value: "",
      validations: [{ required: data?.process_responsible_needed }] },
    [ids.judgementLimitDate]: {
      value: data?.data_limite_julgamento ? fromServerDate(data?.data_limite_julgamento) : "",
      validations: [{ required: true }],
    },
    [ids.observation]: { value: "" }
  });

  useEffect(() => {
    setFormData(initFormData(processData));
  }, [processData]);

  const handleInputChange = (e) => {
    const { id, type } = e.target;
    const value = type === "checkbox" ? e.target.checked : e.target.value;

    const newFormData = {
      ...formData,
      [id]: {
        ...formData[id],
        value: value
      }
    };
    setFormData(newFormData);
    setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
  };

  const handleJudgementLimitDateChange = (value) => {
    const m = moment(value);
    const date = m.isValid() ? m.format(DATE_FORMAT_ISO) : "";

    const updated = {
      ...formData,
      [ids.judgementLimitDate]: {
        ...formData[ids.judgementLimitDate],
        value: date,
      },
    };

    setFormData(updated);

    setInputError((prev) =>
      checkErrors({
        formData: updated,
        inputError: prev,
        name: ids.judgementLimitDate,
      })
    );
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchUsers({});
      } catch (error) {
        console.error(t("system.error.api.loading-data"), error);
      }
    };

    void loadData();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchDepartments({});
      } catch (error) {
        console.error(t("system.error.api.loading-data"), error);
      }
    };

    void loadData();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchProcess({});
      } catch (error) {
        console.error(t("system.error.api.loading-data"), error);
      }
    };

    void loadData();
  }, []);

  const redirectToList = () => {
    navigate(ROUTES.processesPath);
  }

  const fetchProcess = async () => {
    setIsLoading(true);
      try {
        const process = await processesService.find(id);
            setProcessData(process);
      } catch (error) {
        setProcessData([]);
        showToast({ title: t("process.messages.errors.find"), status: "error" });
        redirectToList();
      } finally {
        setIsLoading(false);
      }
  }

  const toDTO = () => ({
    ...processData,
    id_unidade_administrativa_responsavel: formData[ids.department].value,
    id_usuario_responsavel_atual: formData[ids.user].value,
    data_limite_julgamento: toServerDate(formData[ids.judgementLimitDate].value),
    observacoes: formData[ids.observation].value,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormInvalid) return;

    try {
      const data = toDTO();

      await processesService.forward(id, data);

      showToast({ title: t("system.notifications.success"), status: "success" });
      redirectToList();
    } catch (error) {
      console.log(error)
      showToast({ title: t(error), status: "error" });
    }
  };


  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const resUsers = await processesService.lookupUsers();
      const content = resUsers ?? [];
      setUserData(content);
    } catch (error) {
      setUserData({ content: [] });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    setIsLoading(true);
    try {
      const resDepartments = await departmentService.list();
      setDepartmentsData(resDepartments);
    } catch (error) {
      setDepartmentsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex direction="column" minH="89vh" align="center" pt={{sm: "120px", md: "75px"}}>
      <Card mt={"80px"}>
        <CardHeader mb="40px">
          <Flex direction="column">
            <Text color={textColor} fontSize="lg" fontWeight="bold" mb="3px">
              {t("process.forward.form.title")}
            </Text>
            <Text color="gray.400" fontWeight="normal" fontSize="sm">
              {t("process.forward.form.subtitle")}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          {isLoading && (
            <Box
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="rgba(255, 255, 255, 0.6)"
              display="flex"
              justifyContent="center"
              alignItems="center"
              zIndex="overlay"
            >
              <Loading maxH={{ sm: "100px", md: "350px" }} />
            </Box>
          )}
          <form onSubmit={handleSubmit}>
            <Flex direction="column" w="100%">
              <Grid gap="24px">
                <FormControl isRequired isInvalid={inputError[ids.department]}>
                  <FormLabel mb="0" fontSize="xs" fontWeight="bold">
                    {t("process.forward.form.fields.department.label")}
                  </FormLabel>
                  <Select
                    fontSize="sm"
                    id={ids.department}
                    name={ids.department}
                    onChange={handleInputChange}
                  >
                    <option value=""></option>
                    {departmentsData._embedded?.content.map((value, key) => (
                      <option key={value.sigla} value={value.id}>
                        {value.sigla}
                      </option>
                    ))}

                  </Select>
                  <FormErrorMessage>
                    {t(inputError[ids.department])}
                  </FormErrorMessage>
                </FormControl>

                {processData.process_responsible_needed && (
                <FormControl isRequired isInvalid={inputError[ids.user]}>
                  <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                    {t('process.forward.form.fields.responsible.label')}
                  </FormLabel>
                  <SearchSelect
                    items={userData}
                    id={ids.user}
                    displayField="nome_pessoa"
                    valueField="id"
                    placeholder={t('process.forward.form.fields.responsible.placeholder')}
                    value={formData.user}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.user])}
                  </FormErrorMessage>
                </FormControl>
                )}

                {processData.process_judgement_meetings_needed && (
                <FormControl isRequired isInvalid={inputError[ids.judgementLimitDate]}>
                  <FormLabel color={textColor} fontWeight="bold" fontSize="xs" onClick={(e) => e.preventDefault()}>
                    {t('process.forward.form.fields.judgement-limit-date.label')}
                  </FormLabel>
                  <DateTime
                    dateFormat={DATE_FORMAT}
                    timeFormat={false}
                    locale={LOCALE_PT_BR}
                    value={
                      formData[ids.judgementLimitDate].value
                        ? moment(formData[ids.judgementLimitDate].value, DATE_FORMAT_ISO)
                        : ""
                    }
                    renderInput={(props) => (
                      <Input
                        {...props}
                        variant="main"
                        fontSize="sm"
                        placeholder={t('process.forward.form.fields.judgement-limit-date.placeholder')}
                      />
                    )}
                    onChange={handleJudgementLimitDateChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.judgementLimitDate])}
                  </FormErrorMessage>
                </FormControl>
                )}
                <FormControl isInvalid={inputError[ids.observation]}>
                  <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                    {t("process.forward.form.fields.observation.label")}
                  </FormLabel>
                  <Textarea
                    minH="120px"
                    name="reason"
                    id={ids.observation}
                    onChange={handleInputChange}
                    bg={bgTextarea}
                    _placeholder={{color: placeholderColor}}
                    placeholder={t("process.forward.form.fields.observation.placeholder")}
                    fontSize="sm"
                  />
                </FormControl>
              </Grid>
              <Flex justify="space-between">
                <Button
                  variant="light"
                  fontSize="sm"
                  alignSelf="flex-end"
                  mt="24px"
                  w="100px"
                  h="35px"
                  onClick={redirectToList}
                >
                  {t("system.actions.back")}
                </Button>
                <Button
                  variant="dark"
                  fontSize="sm"
                  alignSelf="flex-end"
                  type="submit"
                  mt="24px"
                  w="100px"
                  h="35px"
                  isDisabled={isFormInvalid}
                  _hover={isFormInvalid ? {} : undefined}
                >
                  {t("system.actions.save")}
                </Button>
              </Flex>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default ProcessForward